import './Navbar.css';
import IconLogo from '@/assets/icons/icon-logo.svg?react';
import clsx from 'clsx';
import React, { useCallback, useEffect, useState } from 'react';
import IconArrowLeft from '@/assets/icons/icon-arrow-left-02.svg?react';
import IconChevronLeft from '@/assets/icons/icon-arrow-left.svg?react';
import IconReload from '@/assets/icons/icon-reload.svg?react';
import { IconButton } from '../../buttons/IconButton/IconButton';
import { NavbarProjectTitle } from '../NavbarProjectTitle/NavbarProjectTitle';
import IconRedo from '@/assets/icons/icon-redo.svg?react';
import IconUndo from '@/assets/icons/icon-undo.svg?react';
import { SecondaryButton } from '@/components/buttons/SecondaryButton/SecondaryButton';
import { PublishControl } from '@/components/navbar/PublishControl/PublishControl';
import { IconSimpleButton } from '@/components/buttons/IconSimpleButton/IconSimpleButton';
import { useCraftSerialization } from '@/hooks/useCraftSerialization';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { ResponsivePreviewNav } from '@/components/widgets/ResponsivePreviewNav/ResponsivePreviewNav';
import {
  setPreviewModeEnabled,
  setAnalyticsViewEnabled,
  setFullscreenPreview,
} from '@/store/system/systemSlice';
import {
  selectPreviewModeEnabled,
  selectBreakpoint,
  selectAnalyticsViewEnabled,
} from '@/store/system/selectors';
import { selectIsFeatureEnabled } from '@/store/featureFlags/selectors';
import { AnalyticsActionButton } from '@/components/analytics/AnalyticsActionButton/AnalyticsActionButton';
import IconAnalytics from '@/assets/icons/icon-analytics.svg?react';
import IconAnalyticsFilled from '@/assets/icons/icon-analytics-filled.svg?react';
import { TooltipButton } from '@/components/buttons/TooltipButton/TooltipButton';
import useBreakpoint from '@/hooks/useBreakpoint';
import type { Breakpoint } from '@builder4/shared/types/layout';
import { ROOT_NODE, useEditor } from '@craftjs/core';
import { editorRegistry } from '@/components/userComponents/Text';
import { selectCurrentPage } from '@/store/pages/selectors';
import { Skeleton } from '@/components/ui/skeleton';
import { useMixpanel } from '@/hooks/useMixpanel';
import { EVENT, SOURCE, PROPERTY } from '@/analytics/mixpanel';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { PrimaryButton } from '@/components/buttons/PrimaryButton/PrimaryButton';
import { NavbarPublish } from '@/components/navbar/NavbarPublish/NavbarPublish';

interface NavbarProps {
  className: string;
  variant?: 'builder' | 'analytics';
  loading?: boolean;
  disabled?: boolean;
  onBack?: () => void;
  onExportCsv?: () => void;
}

export const Navbar: React.FC<NavbarProps> = ({
  className,
  variant = 'builder',
  loading = false,
  disabled = false,
  onBack,
  onExportCsv,
}) => {
  const dispatch = useAppDispatch();
  const currentPage = useAppSelector(selectCurrentPage);
  const { store, actions, query } = useEditor();
  const { isLoading } = useCraftSerialization();
  const previewMode = useAppSelector(selectPreviewModeEnabled);
  const analyticsView = useAppSelector(selectAnalyticsViewEnabled);
  const isAnalyticsDashboardEnabled = useAppSelector(selectIsFeatureEnabled('analytics_dashboard'));
  const isCustomDomainPublishingEnabled = useAppSelector(
    selectIsFeatureEnabled('builder4_custom_domain'),
  );
  const [publishOpen, setPublishOpen] = useState(false);
  const { switchBreakpoint } = useBreakpoint();
  const breakpoint = useAppSelector(selectBreakpoint);
  const [initialBreakpoint, setInitialBreakpoint] = useState<Breakpoint>(breakpoint);

  const isLoadingState = loading || !currentPage;
  const track = useMixpanel();

  useEffect(() => {
    if (previewMode) {
      document.body.classList.add('preview-mode');
    } else {
      document.body.classList.remove('preview-mode');
    }
  }, [previewMode]);

  const dashboardHref = (() => {
    const apiMainApp = import.meta.env.VITE_API_MAIN_APP as string | undefined;
    const dashboardPath = '/#/pages';

    if (apiMainApp && /^https?:\/\//.test(apiMainApp)) {
      try {
        return new URL(dashboardPath, apiMainApp).toString();
      } catch {
        // Fall through to error state if URL construction fails
      }
    }

    console.error('Missing or invalid VITE_API_MAIN_APP; dashboard link is disabled.');
    return undefined;
  })();

  const isBreakpointAction = useCallback(
    (type: 'undo' | 'redo') => {
      const pointer = type === 'undo' ? store.history.pointer : store.history.pointer + 1;
      if (store.history.timeline[pointer]) {
        let list = store.history.timeline[pointer].inversePatches;
        if (type === 'redo') {
          list = store.history.timeline[pointer].patches;
        }
        if (list.length) {
          const indexToLook = type === 'undo' ? list.length - 1 : 0;
          const path = [...list[indexToLook].path];
          const propChanged = path.pop();
          return propChanged === 'breakpoint';
        }
      }
      return false;
    },
    [store],
  );

  const handleUndo = () => {
    const breakpointChanged = isBreakpointAction('undo');
    const shouldSlateUndo = isSlateHistoryAction('undo');
    actions.history.undo();

    track(EVENT.UNDO_USED, {
      [PROPERTY.SOURCE]: SOURCE.BUTTON,
    });

    if (breakpointChanged) {
      const breakpoint = query.node(ROOT_NODE).get().data.props.breakpoint;
      switchBreakpoint(breakpoint, true);
    }
    if (shouldSlateUndo) {
      const targetNodeId = getTargetNodeId('undo');
      if (targetNodeId) {
        editorRegistry.get(targetNodeId)?.editor.undo();
      }
    }
  };

  const handleRedo = () => {
    const breakpointChanged = isBreakpointAction('redo');
    const shouldSlateRedo = isSlateHistoryAction('redo');
    actions.history.redo();

    track(EVENT.REDO_USED, {
      [PROPERTY.SOURCE]: SOURCE.BUTTON,
    });

    if (breakpointChanged) {
      const breakpoint = query.node(ROOT_NODE).get().data.props.breakpoint;
      switchBreakpoint(breakpoint, true);
    }
    if (shouldSlateRedo) {
      const targetNodeId = getTargetNodeId('redo');
      if (targetNodeId) {
        editorRegistry.get(targetNodeId)?.editor.redo();
      }
    }
  };

  const isSlateHistoryAction = useCallback(
    (type: 'undo' | 'redo') => {
      const pointer = type === 'undo' ? store.history.pointer : store.history.pointer + 1;
      const state = store.history.timeline[pointer];
      if (!state) {
        return false;
      }
      let patches = state.inversePatches;
      if (type === 'redo') {
        patches = state.patches;
      }
      return patches.some((patch) => patch.path.includes('textStyles'));
    },
    [store],
  );

  const getTargetNodeId = useCallback(
    (type: 'undo' | 'redo'): string => {
      const pointer = store.history.pointer;
      const state = store.history.timeline[pointer];
      if (!state) {
        return '';
      }
      let patches = state.inversePatches;
      if (type === 'redo') {
        patches = state.patches;
      }
      const firstPatch = patches[0];
      if (!firstPatch) {
        return '';
      }

      return firstPatch.path[1] as string;
    },
    [store],
  );

  const handleExitPreview = () => {
    dispatch(setPreviewModeEnabled(false));
    dispatch(setFullscreenPreview(false));
    switchBreakpoint(initialBreakpoint, true);
  };

  const handleTogglePreview = () => {
    // saveCraftJson();
    dispatch(setPreviewModeEnabled(!previewMode));
    setInitialBreakpoint(breakpoint);

    track(EVENT.PREVIEW_CLICKED);
  };

  const handleToggleAnalytics = () => {
    dispatch(setAnalyticsViewEnabled(!analyticsView));
  };

  const handleBack = onBack ?? (() => dispatch(setAnalyticsViewEnabled(false)));

  if (variant === 'analytics') {
    return (
      <div
        className={clsx(
          'bg-neutral-0 relative z-10 flex h-12 w-full items-center justify-between border-b border-neutral-100 px-3',
          className,
        )}
        data-state="Analytics"
      >
        <div className="flex items-center gap-2">
          <IconSimpleButton
            onClick={handleBack}
            icon={IconChevronLeft}
            size={28}
            state="default"
            aria-label="Go back"
            data-qa-selector="analytics-navbar-back-button"
          />
          <span className="text-xs leading-5 font-semibold text-neutral-900">Analytics</span>
        </div>

        <div className="flex items-center gap-2">
          <AnalyticsActionButton
            icon={IconAnalytics}
            selectedIcon={IconAnalyticsFilled}
            label="Analytics"
            state={analyticsView ? 'selected' : 'default'}
            onClick={handleToggleAnalytics}
            data-qa-selector="analytics-navbar-analytics-button"
          />
          <SecondaryButton
            className="w-[156px]"
            onClick={onExportCsv}
            icon={null}
            size="md"
            state="default"
            text="Export to CSV"
            data-qa-selector="analytics-navbar-export-button"
          />
        </div>
      </div>
    );
  }

  return (
    <div
      className={clsx(
        'bg-neutral-0 relative z-10 grid h-13 w-full grid-cols-[1fr_auto_1fr] items-center border-b border-neutral-100 px-3',
        className,
      )}
    >
      {/* Left column */}
      <div className="justify-self-start">
        {!previewMode && !isLoadingState && (
          <div className="flex items-center gap-3">
            {dashboardHref ? (
              <a
                href={dashboardHref}
                className="outline-none"
                data-qa-selector="navbar-dashboard-link"
              >
                <IconLogo className="h-8 w-8 text-neutral-500" />
              </a>
            ) : (
              <div className="outline-none" aria-label="Leadpages dashboard link unavailable">
                <IconLogo className="h-8 w-8 text-neutral-500" />
              </div>
            )}
          </div>
        )}
        {previewMode && (
          <div className="flex items-center gap-2">
            <SecondaryButton
              onClick={handleExitPreview}
              icon={IconArrowLeft}
              size="md"
              state={'default'}
              text={'Back to builder'}
              data-qa-selector="navbar-exit-preview-button"
            />
            <TooltipButton
              showTooltip={true}
              className=""
              icon={IconReload}
              state="default"
              tooltipContent="Reload preview"
              data-qa-selector="navbar-reload-preview-button"
            />
          </div>
        )}
      </div>

      {/* Center column */}
      <div className="justify-self-center">
        {!previewMode && isLoadingState && <Skeleton className="h-5 w-56 rounded-md" />}
        {!previewMode && !isLoadingState && (
          <NavbarProjectTitle
            badge={currentPage?.publishedVersionId ? 'PUBLISHED' : 'DRAFT'}
            className=""
            title={currentPage.name || 'Untitled Page'}
          />
        )}
        {previewMode && <ResponsivePreviewNav state={breakpoint} frameRef={null} />}
      </div>

      {/* Right column */}
      <div className="flex items-center gap-2 justify-self-end">
        {!previewMode && !isLoadingState && !disabled && (
          <div className="flex items-center">
            <IconButton
              onClick={handleUndo}
              className=""
              icon={IconUndo}
              iconSelected={IconUndo}
              state={store.history.timeline[store.history.pointer] ? 'default' : 'disabled'}
              data-qa-selector="navbar-undo-button"
            />
            <IconButton
              onClick={handleRedo}
              className=""
              icon={IconRedo}
              iconSelected={IconRedo}
              state={store.history.timeline[store.history.pointer + 1] ? 'default' : 'disabled'}
              data-qa-selector="navbar-redo-button"
            />
          </div>
        )}

        {!previewMode && !isLoadingState && !disabled && isAnalyticsDashboardEnabled && (
          <AnalyticsActionButton
            icon={IconAnalytics}
            selectedIcon={IconAnalyticsFilled}
            label="Analytics"
            state={analyticsView ? 'selected' : 'default'}
            onClick={handleToggleAnalytics}
            data-qa-selector="navbar-analytics-button"
          />
        )}

        {!previewMode && !isLoadingState && !disabled && (
          <>
            <SecondaryButton
              onClick={handleTogglePreview}
              icon={null}
              size="md"
              state={isLoading ? 'loading' : previewMode ? 'selected' : 'default'}
              text={isLoading ? 'Loading...' : 'Preview'}
              data-qa-selector="navbar-preview-button"
            />
            {isCustomDomainPublishingEnabled ? (
              <PublishControl />
            ) : (
              <DropdownMenu open={publishOpen} onOpenChange={setPublishOpen}>
                <DropdownMenuTrigger className="overflow-hidden rounded-md" asChild>
                  <PrimaryButton
                    icon={null}
                    size="md"
                    state="default"
                    text="Publish"
                    data-qa-selector="navbar-publish-button"
                  />
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="border-none bg-transparent p-0">
                  <NavbarPublish
                    className=""
                    text="Publish"
                    onClose={() => setPublishOpen(false)}
                  />
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </>
        )}
      </div>
    </div>
  );
};
